// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
// --- Actions & Hooks
import { usePMXPricesAndDatesData } from "./hooks/usePMXPricesAndDatesData";
// --- External UI Libraries
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
import { Col, Row } from "antd";
// --- Components
import { PaginationBar } from "../../components/PaginationBar";
import ToastNotificationProvider from "../../components/ToastNotifications";
import { PMXPricesAndDatesGrid } from "./components/grid";
import { PMXPricesAndDatesSearchForm } from "./components/search-form";
import TwoRowsLayout from "./two-row";
// --- Utilities
import { exportToCsvWithHeaders } from "../../utils/exportToCsv";
import { getItemRange, normalizeSpringDataPage } from "../../utils/pagination";
import { parseSearchParams, updateSearchParams } from "../../utils/urlParams";
import { useStoredItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
// --- Config
import { headers, renamedHeaders } from "./config/csvHeaders";
// --- Styles
import "./styles.scss";

// Export range for CSV export
const PMX_EXPORT_RANGE = "0,999";

/**
 * PMX Prices and Dates Page
 *
 * Features:
 * - Displays a paginated list of PMX prices and dates
 * - Provides search and export functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 *
 * @returns {Element}
 * @constructor
 */
export const PMXPricesAndDatesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    fetchPMXPricesAndDates,
    updatePMXPricesAndDates,
    itemsPerPage,
    filterForm,
    pmxPricesAndDatesSort,
  } = usePMXPricesAndDatesData();

  const { totalPages, currentPage, totalItems, pageSizeOptions } = useSelector(
    (s) => s.pmxPricesAndDates,
  );

  useStoredItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.pmxPricesAndDates,
    itemsPerPage,
    rangeKey: "pmxPricesAndDatesRange",
    onSync: updatePMXPricesAndDates,
  });

  // Handle page change for pagination
  const handleChange = async (pageNumber) => {
    if (pageNumber === currentPage) return;

    const nextPageRange = getItemRange(pageNumber, itemsPerPage);
    const data = await fetchPMXPricesAndDates(nextPageRange);

    if (data) {
      const meta = normalizeSpringDataPage(data, itemsPerPage);
      updatePMXPricesAndDates({
        pricesAndDates: data?.content,
        totalPages: meta?.totalPages ?? data?.totalPages,
        currentPage: meta?.currentPage ?? data?.pageable?.pageNumber ?? 0,
        pmxPricesAndDatesRange: nextPageRange,
        totalItems: meta?.totalElements ?? data?.totalElements,
      });
    }
  };

  const handlePageSizeChange = (newSize) => {
    const currentParams = parseSearchParams(location.search);
    updateSearchParams(location, navigate, {
      ...currentParams,
      pageSize: newSize,
    });
  };

  // Export all PMX Prices and Dates to CSV
  const handleExport = async () => {
    const data = await fetchPMXPricesAndDates(PMX_EXPORT_RANGE);

    if (!data) return;

    const filtered = (data?.content ?? []).map((item) =>
      headers.reduce((obj, key) => {
        obj[key] = item[key];
        return obj;
      }, {}),
    );

    exportToCsvWithHeaders(
      filtered,
      headers,
      renamedHeaders,
      "pmxCuratedDatesAndPricing",
    );
  };

  return (
    <>
      <Row className="pmx-page-header">
        <Col>
          <Typography variant="T50" className="pmx-page-title">
            PMX Prices and Dates
          </Typography>
        </Col>
        <Col className="pmx-page-actions">
          <Button
            iconType="fa-solid fa-arrow-down-to-line"
            labelText="Export"
            styleType="link"
            onClick={handleExport}
            disabled={totalItems === 0}
          >
            Export
          </Button>
          <Button
            iconType="fa-solid fa-rotate"
            labelText="Refresh"
            styleType="link"
            onClick={() =>
              updatePMXPricesAndDates({ refreshPMXPricesAndDates: true })
            }
          >
            Refresh
          </Button>
        </Col>
      </Row>
      <TwoRowsLayout>
        <main data-position="top">
          <PMXPricesAndDatesSearchForm />
        </main>
        <main data-position="bottom">
          <PMXPricesAndDatesGrid />
        </main>
      </TwoRowsLayout>
      <PaginationBar
        totalPages={totalPages || 0}
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handleChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
      <ToastNotificationProvider />
    </>
  );
};
